import React, { useEffect, useState } from "react";
import { DOCTOR_HEALTHCARE_FAQS } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Custom HTML sanitization function
function sanitizeHTML(html: string): string {
  if (!html) return "";

  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, "")
    .replace(/javascript:/gi, "")
    .replace(/on\w+="[^"]*"/gi, "")
    .replace(/on\w+='[^']*'/gi, "")
    .replace(/data:(?!image\/)[^;]+;base64[^"'\s]*/gi, "");
}

// Enhanced FAQ data structure with different content types
const COUNTRY_SPECIFIC_FAQS = {
  CHINA: [
    {
      id: 1,
      question:
        "Q1. Can I study MBBS at Peking Union Medical College or follow the Miles MBBS–MD U.S. Pathway?",
      answer:
        "Peking Union Medical College is China’s top-ranked medical institution. However, to practice in India, FMGE/NExT is still mandatory. The Miles Pathway skips this hurdle by delivering a U.S. license—globally recognized and FMGE-exempt.",
      contentType: "text",
    },
    {
      id: 2,
      question:
        "Q2. Is Southern Medical University recognized by the NMC (India)?",
      answer:
        "Yes, Southern Medical University is NMC-listed. However, you’ll still need to pass FMGE to practice in India. Miles removes this roadblock with a direct U.S. licensure pathway.",
      contentType: "text",
    },
    {
      id: 3,
      question:
        "Q3. Can Harbin Medical University or Nanjing Medical University help with U.S. medical residency?",
      answer:
        "Not directly. Chinese universities like Harbin or Nanjing don’t offer U.S. clinical exposure or Step 1/2 prep. Miles includes guaranteed U.S. hospital rotations and full USMLE coaching, increasing residency match chances exponentially.",
      contentType: "text",
    },
    {
      id: 4,
      question:
        "Q4. Will I need to clear FMGE after studying at Tianjin Medical University?",
      answer:
        "Yes, despite being well-ranked, Tianjin Medical University graduates must clear FMGE/NExT to practice in India. Miles grads, on the other hand, bypass FMGE via U.S. licensure.",
      contentType: "text",
    },
    {
      id: 5,
      question:
        "Q5. Do Chinese MBBS universities offer real U.S. clinical rotations or Step 2 CK prep?",
      answer:
        " No. Institutions like Peking Union Medical College and Nanjing Medical University don’t offer U.S. clinicals or Step 2 CK support. Miles provides both, leading to better preparedness for U.S. practice.",
      contentType: "text",
    },
    {
      id: 6,
      question:
        "Q6. Can I use a Chinese MBBS degree to match into U.S. residency?",
      answer:
        "  It’s very difficult. Without U.S. clinical experience and USMLE coaching, chances are slim. Miles has a 100% U.S.-modeled pathway with direct access to rotations and structured match support.",
      contentType: "text",
    },
    {
      id: 7,
      question:
        "Q7. Is the MBBS from Harbin or Tianjin cheaper than the Miles Pathway?",
      answer:
        " Yes, but there's a tradeoff. While Chinese MBBS may cost ₹25–35L, it lacks long-term ROI due to FMGE delays and lower salaries. Miles, though costlier upfront (₹1 Cr), delivers a ₹2 Cr/year U.S. salary after residency.",
      contentType: "text",
    },
    {
      id: 8,
      question:
        "Q8. Will I need to learn Mandarin for clinical years at Southern Medical University or Harbin?",
      answer:
        " Yes. Most Chinese MBBS programs require you to communicate in Mandarin during clinicals. In contrast, the Miles Pathway is 100% English, with no additional language requirement.",
      contentType: "text",
    },
    {
      id: 9,
      question:
        "Q9. Are degrees from Nanjing Medical University or Tianjin Medical University accepted globally?",
      answer:
        " Limited acceptance outside Asia. Chinese degrees are not designed for U.S./UK licensure. Miles offers a U.S.-aligned MD recognized in the U.S., UK, Canada, UAE, and India (FMGE-exempt).",
      contentType: "text",
    },
  ],
  RUSSIA: [
    {
      id: 1,
      question:
        "Q1. Should I choose Orenburg State Medical University or Miles MBBS–MD U.S. Pathway?",
      answer:
        " While Orenburg offers a low-cost MBBS degree, students must clear FMGE/NExT to practice in India. In contrast, the Miles program provides a direct path to a U.S. medical license, exempting graduates from FMGE.",
      contentType: "text",
    },
    {
      id: 2,
      question:
        "Q2. Is MBBS from Perm State Medical University valid in India?",
      answer:
        "Yes, but you must clear the FMGE exam, which has less than a 20% pass rate. The Miles program bypasses this by securing you a U.S. license, which is accepted in India under NMC guidelines.",
      contentType: "text",
    },
    {
      id: 3,
      question: "Q3. Is Mari State University good for Indian students?",
      answer:
        " Mari State is recognized, but students often face issues like language barriers and lack of U.S.-standard training. Miles offers full U.S.-style MD education in English with U.S. clinical rotations.",
      contentType: "text",
    },
    {
      id: 4,
      question:
        "Q4. Can I get a U.S. license after studying at Pskov State University or Tver State Medical University?",
      answer:
        "It’s extremely difficult. These universities do not offer U.S. curriculum or USMLE training. With Miles, your entire pathway is built for U.S. licensure from Day 1.",
      contentType: "text",
    },
    {
      id: 5,
      question:
        "Q5. Do graduates from Ural State Medical University or Dagestan State Medical University need to write FMGE?",
      answer:
        "Yes, FMGE is mandatory. However, Miles students are FMGE-exempt if they get their U.S. license through our program.",
      contentType: "text",
    },
    {
      id: 6,
      question:
        "Q6. How does studying at Kemerovo State Medical University compare to Miles MBBS–MD?",
      answer:
        " Kemerovo offers a standard Russian MBBS, but graduates face difficulty getting licensed abroad. With Miles, you get U.S. clinical rotations, a globally valid MD degree, and $250K salary potential.",
      contentType: "text",
    },
    {
      id: 7,
      question:
        "Q7. What about studying at Irkutsk State Medical University or Samara State Medical University?",
      answer:
        "Both are popular but still require FMGE and do not provide U.S. clinical experience. Miles includes guaranteed U.S. rotations and full USMLE preparation.",
      contentType: "text",
    },
    {
      id: 8,
      question:
        "Q8. Is Tula State University or Chuvash State University recognized by NMC?",
      answer:
        "Yes, they’re listed. But remember, NMC recognition does not guarantee your license unless you clear FMGE. Miles students get licensed in the U.S., which automatically meets NMC foreign license standards.",
      contentType: "text",
    },
    {
      id: 9,
      question:
        "Q9. Does Pavlov First Saint Petersburg State Medical University help with U.S. residency?",
      answer:
        "No. While Pavlov is one of the top Russian schools, it does not provide U.S. rotations or Step 1/2 prep. The Miles pathway is purpose-built for U.S. residency and licensure.",
      contentType: "text",
    },
    {
      id: 10,
      question:
        "Q10. Is there an advantage in choosing People’s Friendship University or Pirogov Medical University over Miles?",
      answer:
        "Not really. Both require FMGE, and graduates still need to spend years and money preparing for licensure. Miles students go directly into paid U.S. residencies and earn ₹50L–₹2Cr annually.",
      contentType: "text",
    },
    {
      id: 11,
      question:
        "Q11. Can I get into U.S. residency from Sechenov First Moscow State Medical University or Kazan State Medical University?",
      answer:
        "It’s very rare. These universities don’t provide the support system or U.S. clinical credentials needed for U.S. residency. Miles offers structured support, guaranteed rotations, and a pathway to a U.S. license",
      contentType: "text",
    },
    {
      id: 12,
      question:
        "Q12. What is the cost difference between Kazan Federal University and Miles?",
      answer:
        "Kazan may cost ₹30–₹40L over 6 years, but has no U.S. rotations, no residency prep, and requires FMGE. Miles costs ₹20L/year (₹1Cr total), but provides full support to earn ₹2Cr/year as a licensed U.S. doctor.",
      contentType: "text",
    },
    {
      id: 13,
      question:
        "Q13. Is MBBS from Volgograd State Medical University or Smolensk State Medical University globally recognized?",
      answer:
        " They’re recognized in Russia and India (after FMGE). However, a U.S. MD degree from Miles is globally recognized without extra licensing hurdles in many countries.",
      contentType: "text",
    },
    {
      id: 14,
      question: "Q14. Are U.S. licenses accepted in India?",
      answer:
        " Yes. If you hold a U.S. medical license from the Miles pathway, you're exempt from FMGE/NExT and can register with NMC under the foreign license clause.",
      contentType: "text",
    },
  ],
  NEPAL: [
    {
      id: 1,
      question:
        "Q1. Can I study MBBS at KIST Medical College or take the Miles MBBS–MD U.S. Pathway?",
      answer:
        " KIST Medical College is a respected MBBS option in Nepal, but you’ll need to clear FMGE/NExT to practice in India. With Miles, you bypass FMGE entirely and train for U.S. medical licensure—with global mobility.",
      contentType: "text",
    },
    {
      id: 2,
      question:
        "Q2. Is Kathmandu University School of Medical Sciences (KUSMS) NMC-approved?",
      answer:
        " Yes, KUSMS is NMC-recognized. However, like other Nepal MBBS colleges, it lacks USMLE coaching and U.S. clinical exposure. Miles integrates both—leading to a direct U.S. residency and license.",
      contentType: "text",
    },
    {
      id: 3,
      question:
        "Q3. Does studying at Tribhuvan University’s Institute of Medicine (IOM) allow me to practice in the U.S.?",
      answer:
        " Not directly. IOM and other Nepal colleges (like Patan Academy or Kathmandu Medical College) do not provide USMLE prep or U.S. rotations. Miles includes these essentials—making U.S. licensure realistic.",
      contentType: "text",
    },
    {
      id: 4,
      question:
        "Q4. What happens after I graduate from Nobel Medical College or Chitwan Medical College?",
      answer:
        "  You’ll need to pass FMGE/NExT to work in India. U.S. or UK practice requires additional exams and clinicals. Miles MD graduates are FMGE-exempt and U.S. residency-eligible, ensuring global flexibility.",
      contentType: "text",
    },
    {
      id: 5,
      question:
        "Q5. Are degrees from Nepal Medical College or Devdaha Medical College globally accepted?",
      answer:
        "  They are WHO-listed but not automatically recognized in the U.S., UK, or Canada. Miles’ U.S. MD degree is globally portable, including India, UAE, UK, and North America.",
      contentType: "text",
    },
    {
      id: 6,
      question:
        "Q6. Can I get U.S. residency after studying at colleges like B.P. Koirala Institute or Gandaki Medical College?",
      answer:
        " Highly unlikely. Nepali colleges do not prepare students for USMLE or provide clinical placements in the U.S.. Miles offers guaranteed U.S. rotations and full residency prep.",
      contentType: "text",
    },
    {
      id: 7,
      question:
        "Q7. Is Nepalgunj or Janaki Medical College more cost-effective than Miles?",
      answer:
        " Yes, MBBS in Nepal costs ₹30–40L, while Miles is ₹1 Cr. However, Miles delivers far higher returns—earning ₹4–6L/month during residency and ₹2 Cr/year post-residency.",
      contentType: "text",
    },
    {
      id: 8,
      question:
        "Q8. Is language a barrier in Nepal for MBBS clinical training?",
      answer:
        " Yes. Most programs (e.g., Lumbini Medical College, College of Medical Sciences Nepal) require Nepali or Hindi proficiency in hospitals. Miles’ Pathway is 100% English—no local language required.",
      contentType: "text",
    },
    {
      id: 9,
      question:
        "Q9. Will studying at Universal College of Medical Sciences or Birat Medical College exempt me from FMGE?",
      answer:
        " No. All MBBS graduates from Nepal must clear FMGE/NExT to practice in India. Miles students skip FMGE completely by earning a U.S. medical license.",
      contentType: "text",
    },
  ],
  GEORGIA: [
    {
      id: 1,
      question:
        "Q1. Can I study MBBS at Tbilisi State Medical University or Miles MBBS–MD Pathway?",
      answer:
        "Tbilisi State Medical University provides an affordable MBBS, but graduates must clear FMGE/NExT to practice in India. With Miles, you pursue a U.S. medical license, exempting you from FMGE and ensuring a global medical career.",
      contentType: "text",
    },
    {
      id: 2,
      question:
        "Q2. Is Caucasus International University (CIU) recognized in India?",
      answer:
        "CIU is NMC-recognized, but you still need to pass FMGE/NExT. Miles bypasses this requirement by delivering a U.S. MD degree and clinical training, leading directly to a U.S. medical license.",
      contentType: "text",
    },
    {
      id: 3,
      question:
        "Q3. Can Georgian universities like David Tvildiani Medical University (DTMU) or Geomedi Medical University help with U.S. recruitment?",
      answer:
        "Although popular, Georgian universities don’t provide USMLE prep or U.S. clinical rotations. Miles includes guaranteed U.S. rotations and full residency support, making U.S. licensure feasible.",
      contentType: "text",
    },
    {
      id: 4,
      question:
        "Q4. What about MD or residency after MBBS from Georgian universities?",
      answer:
        "While options may exist, Georgian postgraduate degrees aren’t recognized in the U.S. Miles leads you directly to a U.S. residency and medical license — the pathway is fully U.S.-aligned.",
      contentType: "text",
    },
    {
      id: 5,
      question:
        "Q5. Will I have to clear FMGE after studying at New Vision University or Tbilisi Medical Academy?",
      answer:
        "Yes. Learning medicine in Georgia still means taking FMGE/NExT to practice in India. Miles students are exempt from FMGE upon obtaining a U.S. medical license.",
      contentType: "text",
    },
    {
      id: 6,
      question:
        "Q6. How does studying at Batumi Shota Rustaveli State University or Akaki Tsereteli State University compare?",
      answer:
        "Though cost-effective, these Georgian degrees lack U.S. standards and clinical exposure. Miles offers 100% English education, USMLE prep, and real-world U.S. clinical training.",
      contentType: "text",
    },
    {
      id: 7,
      question:
        "Q7. Is an MBBS from Georgian National University (SEU) accepted globally?",
      answer:
        "While recognized by NMC/WHO, it does not align with USMLE or U.S. residency necessities. Miles, on the other hand, is built around U.S. licensure and draws international recognition.",
      contentType: "text",
    },
    {
      id: 8,
      question:
        "Q8. Is the tuition at Tbilisi State Medical University's U.S. Pattern course cheaper than Miles?",
      answer:
        "Though the U.S.-patterned course seems appealing, it still lacks U.S. clinical rotations and residency support. Miles offers guaranteed clinical placements and a clear path to U.S. practice.",
      contentType: "text",
    },
    {
      id: 9,
      question:
        "Q9. Will studying at David Agmashenebeli University of Georgia help me get licensed in India?",
      answer:
        "You can practice in India after clearing FMGE/NExT. Miles avoids this entire process with a U.S. license route, enabling both U.S. and Indian registration without FMGE.",
      contentType: "text",
    },
    {
      id: 10,
      question:
        "Q10. Do I need to learn Georgian or another language for clinicals?",
      answer:
        "Yes — most Georgian programs require proficiency in Georgian or Russian during clinical years. The Miles Pathway is 100% English, with no additional language requirements.",
      contentType: "text",
    },
    {
      id: 11,
      question:
        "Q11. Can graduates from Illia State University or Petre Shotadze Tbilisi Medical Academy match into U.S. residency?",
      answer:
        "Very unlikely. Without USMLE prep and U.S. clinical rotations, U.S. residency remains out of reach. Miles includes both — making residency realistic for Indian students.",
      contentType: "text",
    },
  ],
  BANGLADESH: [
    {
      id: 1,
      question:
        "Q1. Can I study MBBS at Dhaka National Medical College or follow the Miles MBBS–MD U.S. Pathway?",
      answer:
        "Dhaka National Medical College offers a popular MBBS in Bangladesh, but Indian students still need to clear FMGE/NExT to practice back home. With Miles, you’re exempt from FMGE, receive U.S. clinical training, and earn a U.S. medical license—accepted globally.",
      contentType: "text",
    },
    {
      id: 2,
      question: "Q2. Is Sylhet Women’s Medical College NMC-recognized?",
      answer:
        "Yes, Sylhet Women’s Medical College is NMC-listed, meaning you can sit for the FMGE. However, unlike Miles, it lacks U.S. rotations and Step prep—making U.S. licensure difficult.",
      contentType: "text",
    },
    {
      id: 3,
      question:
        "Q3. Does studying at Mainamoti or Enam Medical College help with U.S. residency?",
      answer:
        "No. Bangladeshi colleges like Mainamoti, Enam, or North East Medical College don’t offer USMLE preparation or U.S. hospital rotations. Miles includes both, giving you real chances at U.S. residency and a ₹2 Cr/year salary.",
      contentType: "text",
    },
    {
      id: 4,
      question:
        "Q4. Will I have to clear FMGE after graduating from institutions like TMSS or Parkview Medical College?",
      answer:
        "Yes. All Bangladeshi MBBS graduates, regardless of college (e.g., TMSS, Parkview), must pass FMGE/NExT for Indian registration. Miles Pathway students skip this entirely through their U.S. license.",
      contentType: "text",
    },
    {
      id: 5,
      question:
        "Q5. Are colleges like Ibrahim Medical College or Delta Medical College aligned with U.S. standards?",
      answer:
        "Not really. While these colleges offer MBBS in English, they do not provide U.S. clinical exposure or Step 1/2 training. Miles is fully U.S.-aligned, with residency and license support built in.",
      contentType: "text",
    },
    {
      id: 6,
      question:
        "Q6. Can I use a degree from Jalalabad Ragib Rabeya Medical College for global practice?",
      answer:
        "Limited. Bangladeshi MBBS degrees are typically not accepted in the U.S., UK, or Canada without extra steps. Miles’ U.S. MD is directly recognized in major healthcare systems.",
      contentType: "text",
    },
    {
      id: 7,
      question:
        "Q7. Do colleges like Universal Medical College or Ad-Din Women’s Medical College offer better ROI than Miles?",
      answer:
        "They’re more affordable short-term (₹25–35L), but limited in career scope. Miles, at ₹1 Cr total, delivers ₹4–6L/month during residency and ₹2 Cr/year post-residency—far superior ROI.",
      contentType: "text",
    },
    {
      id: 8,
      question:
        "Q8. Will I need to learn Bangla for hospital training in Bangladesh?",
      answer:
        "Yes. Clinical interactions in most Bangladeshi colleges (e.g., Faridpur Diabetes, Popular Medical, Gonoshasthaya) require Bangla language skills. The Miles Pathway is 100% English, no extra languages required.",
      contentType: "text",
    },
    {
      id: 9,
      question:
        "Q9. Are degrees from Tairunnessa Memorial or Holy Family Red Crescent accepted in the U.S.?",
      answer:
        "No. These colleges offer quality regional education but aren’t designed for U.S. licensure. Miles graduates, with a U.S. license, are eligible in the U.S., India (FMGE-exempt), UK, UAE, and more.",
      contentType: "text",
    },
  ],
} as const;

type CountryKey = keyof typeof COUNTRY_SPECIFIC_FAQS;

const DoctorFAQs: React.FC = () => {
  const [activeCountry, setActiveCountry] = useState<string>("GENERIC");

  // Control which accordion item is open; undefined = all closed
  const [openItem, setOpenItem] = useState<string | undefined>(undefined);

  // Close any open item when switching tabs/countries
  useEffect(() => {
    setOpenItem(undefined);
  }, [activeCountry]);

  const countries = [
    "GENERIC",
    "CHINA",
    "RUSSIA",
    "NEPAL",
    "GEORGIA",
    "BANGLADESH",
  ] as const;

  const renderGeneric = () => (
    <Accordion
      type="single"
      collapsible
      className="w-full md:px-14"
      value={openItem}
      onValueChange={setOpenItem}
    >
      {DOCTOR_HEALTHCARE_FAQS.map((item: any) => (
        <AccordionItem
          key={item.id}
          value={`item-${item.id}`}
          className="bg-[#fff] leading-[1.5rem] text-[#253B80] rounded-[14px] relative z-10 mb-[18px] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)]"
        >
          <AccordionTrigger className="hover:no-underline font-semibold md:text-[22px] text-[16px] text-left md:leading-[32px] leading-5 px-[20px] py-[18px] faq_shadow rounded-[14px]">
            {item.title}
          </AccordionTrigger>
          <AccordionContent className="content font-normal md:text-[18px] text-[16px] md:leading-[30px] leading-[22px] text-[#030F1C]">
            <div className="px-[20px] py-[5px]">
              {item.questions && item.questions.length > 0 ? (
                item.questions.map((question: any) => {
                  const clean = sanitizeHTML(String(question.answer ?? ""));
                  return (
                    <div key={question.id} className="mb-4">
                      <p className="md:text-[18px] text-[14px] font-normal leading-[1.4] text-[#030F1C]">
                        <span className="font-bold">
                          {question.question}
                          <br />
                          <br />
                        </span>
                        <span
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: clean }}
                        />
                      </p>
                    </div>
                  );
                })
              ) : (
                <p className="italic text-sm">Coming soon...</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  const renderCountry = (country: CountryKey) => (
    <Accordion
      type="single"
      collapsible
      className="w-full md:px-14"
      value={openItem}
      onValueChange={setOpenItem}
    >
      {COUNTRY_SPECIFIC_FAQS[country].map((item) => (
        <AccordionItem
          key={item.id}
          value={`item-${item.id}`}
          className="bg-[#fff] leading-[1.5rem] text-[#253B80] rounded-[14px] relative z-10 mb-[18px] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)]"
        >
          <AccordionTrigger className="hover:no-underline font-semibold md:text-[22px] text-[16px] text-left md:leading-[32px] leading-5 px-[20px] py-[18px] faq_shadow rounded-[14px]">
            {item.question}
          </AccordionTrigger>
          <AccordionContent className="content font-normal md:text-[18px] text-[16px] md:leading-[30px] leading-5 text-[#030F1C]">
            <div className="px-[20px] py-[5px]">{item.answer}</div>
          </AccordionContent>
        </AccordionItem>
      ))}

      {/* Country-specific extra items (kept as in your original) */}
      {country === "CHINA" && (
        <AccordionItem
          value="item-10"
          className="bg-white leading-[1.5rem] text-[#253B80] rounded-[14px] relative z-10 mb-[18px] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)]"
        >
          <AccordionTrigger className="hover:no-underline font-semibold md:text-[22px] text-[16px] text-left md:leading-[32px] leading-5 px-[20px] py-[18px] faq_shadow rounded-[14px]">
            Q10. Can I practice medicine in India after a Chinese MBBS?
          </AccordionTrigger>
          <AccordionContent className="content font-normal md:text-[18px] text-[16px] md:leading-[30px] leading-[22px] text-[#030F1C]">
            <div className="px-[20px] py-[5px]">
              <p className="mb-4">
                <strong>Yes</strong> — but only after clearing FMGE/NExT, which
                has a &lt;20% pass rate. Miles MD holders are eligible to
                register in India without FMGE if they hold a U.S. license.
              </p>

              <p className="font-semibold">
                Considering Peking Union Medical College, Nanjing, Harbin, or
                Tianjin?
              </p>
              <p className="mb-4">
                These Chinese institutions offer solid MBBS training, but don’t
                offer USMLE prep or U.S. clinical exposure—both critical for a
                global medical career.
              </p>

              <p className="font-semibold mt-4 mb-2">
                China MBBS vs 🇺🇸 Miles MBBS–MD U.S. Pathway
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border font-semibold">Feature</th>
                      <th className="p-3 border font-semibold">
                        Top Chinese Medical Universities (e.g., PUMC, SMU,
                        Nanjing, Harbin, Tianjin)
                      </th>
                      <th className="p-3 border font-semibold">
                        Miles MBBS–MD U.S. Pathway
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      [
                        "Language of Instruction",
                        "Chinese + Partial English",
                        "100% English",
                      ],
                      [
                        "FMGE/NExT Requirement (India)",
                        "Yes",
                        "No (U.S. license exemption)",
                      ],
                      ["U.S. Clinical Rotations", "No", "Yes"],
                      ["USMLE Step 1 & 2 Coaching", "No", "Yes"],
                      ["U.S. Residency Match Support", "No", "Yes"],
                      ["U.S. Medical License", "No", "Yes"],
                      [
                        "Tuition Cost",
                        "₹25–35L (total)",
                        "₹1 Cr (₹20L/year × 5)",
                      ],
                      [
                        "Post-Residency Salary",
                        "₹10–15 LPA (India)",
                        "₹2 Cr/year (U.S.)",
                      ],
                      [
                        "Global Licensing",
                        "Limited to China/India",
                        "U.S., India, UK, UAE, Canada",
                      ],
                    ].map(([feature, china, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{feature}</td>
                        <td className="p-3 border">{china}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="font-semibold mt-4 mb-2">Return on Investment</p>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border font-semibold">Metric</th>
                      <th className="p-3 border font-semibold">China MBBS</th>
                      <th className="p-3 border font-semibold">
                        Miles MBBS–MD U.S. Pathway
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Total Cost", "₹25–35L", "₹1 Cr"],
                      ["FMGE/NExT", "Required", "Exempt (U.S. license)"],
                      ["Earning During Residency", "₹0", "₹4–6 Lakh/month"],
                      [
                        "Post-Residency Salary",
                        "₹10–15 LPA (in India)",
                        "₹2 Cr/year",
                      ],
                    ].map(([metric, china, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{metric}</td>
                        <td className="p-3 border">{china}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {country === "RUSSIA" && (
        <AccordionItem
          value="item-15"
          className="bg-white leading-[1.5rem] text-[#253B80] rounded-[14px] relative z-10 mb-[18px] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)]"
        >
          <AccordionTrigger className="hover:no-underline font-semibold md:text-[22px] text-[16px] text-left md:leading-[32px] leading-5 px-[20px] py-[18px] faq_shadow rounded-[14px]">
            Q15. What support does Miles provide that Russian universities
            don’t?
          </AccordionTrigger>
          <AccordionContent className="content font-normal md:text-[18px] text-[16px] md:leading-[30px] leading-[22px] text-[#030F1C]">
            <div className="px-[20px] py-[5px]">
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>U.S. MD degree</li>
                <li>U.S. clinical rotations</li>
                <li>USMLE prep</li>
                <li>Residency matching support</li>
                <li>No FMGE required</li>
                <li>U.S. visa and licensing assistance</li>
                <li>₹2Cr salary potential</li>
              </ul>

              <p className="font-semibold mt-4">
                Considering MBBS at Orenburg, Tula, Mari or Sechenov University?
              </p>
              <p>
                Russia offers a degree. Miles offers a license and a career.
              </p>

              <p className="mt-4">Thousands go to:</p>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>Perm State Medical University</li>
                <li>Tver State Medical University</li>
                <li>Kabardino Balkarian State Medical University</li>
                <li>Pirogov Medical University</li>
                <li>Volgograd State Medical University</li>
              </ul>
              <p>…but only a few become practicing doctors due to FMGE</p>

              <p className="font-semibold mt-6 mb-2">
                Russia MBBS vs Miles MBBS–MD Pathway
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100 text-[#030F1C]">
                    <tr>
                      <th className="p-3 font-semibold border">Feature</th>
                      <th className="p-3 font-semibold border">
                        Russian Universities (e.g. Kazan, Crimea, Pavlov)
                      </th>
                      <th className="p-3 font-semibold border">
                        Miles MBBS–MD U.S. Pathway
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Language", "Russian/English mix", "100% English"],
                      ["FMGE Required", "Yes", "No (U.S. license)"],
                      ["U.S. Clinicals", "None", "Yes"],
                      ["Residency Support", "No", "Yes"],
                      ["USMLE Prep", "Self", "Included"],
                      [
                        "License Validity",
                        "Only after FMGE (India)",
                        "Global (India, UAE, UK, etc.)",
                      ],
                      ["Cost", "₹25–40L", "₹1 Cr"],
                      ["ROI", "Uncertain", "₹2Cr/year after licensure"],
                    ].map(([feature, russian, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{feature}</td>
                        <td className="p-3 border">{russian}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="font-semibold mt-6 mb-2">Career Outcomes</p>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100 text-[#030F1C]">
                    <tr>
                      <th className="p-3 font-semibold border">Milestone</th>
                      <th className="p-3 font-semibold border">Russian MBBS</th>
                      <th className="p-3 font-semibold border">
                        Miles MBBS–MD
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Cost", "₹30L", "₹1 Cr"],
                      ["Residency", "No", "Yes"],
                      ["During Residency", "0", "₹4–6L/month"],
                      ["After Residency", "₹10–15L/year", "₹2Cr/year"],
                    ].map(([milestone, russian, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{milestone}</td>
                        <td className="p-3 border">{russian}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {country === "NEPAL" && (
        <AccordionItem
          value="item-10"
          className="bg-white leading-[1.5rem] text-[#253B80] rounded-[14px] relative z-10 mb-[18px] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)]"
        >
          <AccordionTrigger className="hover:no-underline font-semibold md:text-[22px] text-[16px] text-left md:leading-[32px] leading-5 px-[20px] py-[18px] faq_shadow rounded-[14px]">
            Q10. Can I work in the U.S. after graduating from Kathmandu
            University or Patan Academy of Health Sciences?
          </AccordionTrigger>
          <AccordionContent className="content font-normal md:text-[18px] text-[16px] md:leading-[30px] leading-[22px] text-[#030F1C]">
            <div className="px-[20px] py-[5px]">
              <p className="mb-4">
                <strong>Not directly.</strong> You’ll still need USMLE, U.S.
                rotations, and years of licensing work. With Miles, the U.S.
                pathway is built in—from Step 1 prep to residency and licensing.
              </p>

              <p className="font-semibold">
                Considering IOM, KU, KIST, or Nobel Medical College?
              </p>
              <p className="mb-4">
                Nepal MBBS programs are cost-effective and well-respected in
                South Asia—but they don’t include U.S. clinical experience,
                USMLE prep, or guaranteed residency. That’s where Miles stands
                apart.
              </p>

              <p className="font-semibold mt-4 mb-2">
                Nepal MBBS vs 🇺🇸 Miles MBBS–MD U.S. Pathway
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border font-semibold">Feature</th>
                      <th className="p-3 border font-semibold">
                        Top Nepali Medical Colleges (e.g., IOM, KUSMS, Nobel,
                        Chitwan)
                      </th>
                      <th className="p-3 border font-semibold">
                        Miles MBBS–MD U.S. Pathway
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      [
                        "Language of Instruction",
                        "English + Nepali (clinicals)",
                        "100% English",
                      ],
                      [
                        "FMGE/NExT Requirement (India)",
                        "Yes",
                        "No (U.S. license exemption)",
                      ],
                      ["U.S. Clinical Rotations", "No", "Yes"],
                      ["USMLE Step 1 & 2 Prep", "No", "Yes"],
                      ["Residency Support (U.S.)", "No", "Yes"],
                      [
                        "Global Licensing",
                        "No (Only Nepal/India)",
                        "U.S., UK, UAE, Canada, India etc",
                      ],
                      ["Tuition Cost", "₹30–40L", "₹1 Cr (₹20L/year × 5)"],
                      [
                        "Post-Residency Salary",
                        "₹10–15 LPA (India)",
                        "₹2 Cr/year (U.S.)",
                      ],
                    ].map(([feature, nepal, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{feature}</td>
                        <td className="p-3 border">{nepal}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="font-semibold mt-4 mb-2">Return on Investment</p>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border font-semibold">Metric</th>
                      <th className="p-3 border font-semibold">Nepal MBBS</th>
                      <th className="p-3 border font-semibold">
                        Miles MBBS–MD U.S. Pathway
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Total Cost", "₹30–40L", "₹1 Cr"],
                      ["FMGE/NExT", "Required", "Exempt"],
                      ["Earnings During Residency", "₹0", "₹4–6L/month"],
                      ["Post-Residency Salary", "₹10–15 LPA", "₹2 Cr/year"],
                    ].map(([metric, nepal, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{metric}</td>
                        <td className="p-3 border">{nepal}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {country === "GEORGIA" && (
        <AccordionItem
          value="item-12"
          className="bg-white leading-[1.5rem] text-[#253B80] rounded-[14px] relative z-10 mb-[18px] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)]"
        >
          <AccordionTrigger className="hover:no-underline font-semibold md:text-[22px] text-[16px] text-left md:leading-[32px] leading-5 px-[20px] py-[18px] faq_shadow rounded-[14px]">
            Q12. Is the MD from Miles better than that from Georgian
            universities like Alte Medical University, Grigol Robakidze
            University, or East European University?
          </AccordionTrigger>
          <AccordionContent className="content font-normal md:text-[18px] text-[16px] md:leading-[30px] leading-[22px] text-[#030F1C]">
            <div className="px-[20px] py-[5px]">
              <p className="mb-4">
                <strong>Absolutely.</strong> While Georgian MDs require FMGE and
                lack universal recognition, a Miles U.S.-aligned MD leads to a
                medical license accepted in the U.S., India (without FMGE), UAE,
                and beyond.
              </p>

              <p className="font-semibold">
                Considering Tbilisi State Medical University, Geomedi, DTMU, or
                CIU?
              </p>
              <p>
                These universities provide MBBS, but that’s just the beginning.
                Without strong USMLE preparation, U.S. clinical training, or a
                structured path to residency, a global medical career remains
                out of reach.
              </p>

              <p className="font-semibold mt-6 mb-2">
                Georgia MBBS vs Miles MBBS–MD U.S. Pathway
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border font-semibold">Feature</th>
                      <th className="p-3 border font-semibold">
                        Georgian Universities (e.g., TSMU, DTMU, Geomedi, CIU)
                      </th>
                      <th className="p-3 border font-semibold">
                        Miles MBBS–MD U.S. Pathway
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      [
                        "Language of Instruction",
                        "Georgian / Russian + English",
                        "100% English",
                      ],
                      [
                        "FMGE/NExT Required for Indian Practice",
                        "Yes",
                        "No (U.S. license exemption)",
                      ],
                      ["U.S. Clinical Rotations", "No", "Yes"],
                      ["USMLE Step 1 & 2 Preparation", "No", "Yes"],
                      ["Residency Match & Support", "No", "Yes"],
                      ["U.S. Medical License Possibility", "No", "Yes"],
                      [
                        "Cost",
                        "₹25–35L (over 5–6 years)",
                        "₹1 Cr (₹20L/year × 5)",
                      ],
                      [
                        "Salary (After Residency)",
                        "₹10–15 LPA (in India)",
                        "₹2 Cr/year",
                      ],
                      [
                        "Global Licensing",
                        "Limited",
                        "U.S., India, UAE, Canada, UK",
                      ],
                    ].map(([feature, georgia, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{feature}</td>
                        <td className="p-3 border">{georgia}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="font-semibold mt-4 mb-2">Return on Investment</p>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border font-semibold">Metric</th>
                      <th className="p-3 border font-semibold">Georgia MBBS</th>
                      <th className="p-3 border font-semibold">
                        Miles MBBS–MD U.S. Pathway
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Total Cost", "₹25–35L", "₹1 Cr"],
                      ["FMGE/NExT", "Required", "No (U.S. license)"],
                      [
                        "Earning during Residency",
                        "0",
                        "₹4–6 Lakh/month (U.S.)",
                      ],
                      [
                        "Post-Residency Salary",
                        "₹10–15 LPA (in India)",
                        "₹2 Cr/year",
                      ],
                    ].map(([metric, georgia, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{metric}</td>
                        <td className="p-3 border">{georgia}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {country === "BANGLADESH" && (
        <AccordionItem
          value="item-10"
          className="bg-white leading-[1.5rem] text-[#253B80] rounded-[14px] relative z-10 mb-[18px] shadow-[99px_131px_46px_0px_rgba(4,84,175,0),64px_84px_42px_0px_rgba(4,84,175,0.01),36px_47px_35px_0px_rgba(4,84,175,0.05),16px_21px_26px_0px_rgba(4,84,175,0.09),4px_5px_14px_0px_rgba(4,84,175,0.1)]"
        >
          <AccordionTrigger className="hover:no-underline font-semibold md:text-[22px] text-[16px] text-left md:leading-[32px] leading-5 px-[20px] py-[18px] faq_shadow rounded-[14px]">
            Q10. Can I practice in India after completing MBBS at Barind Medical
            College or TMSS?
          </AccordionTrigger>
          <AccordionContent className="content font-normal md:text-[18px] text-[16px] md:leading-[30px] leading-[22px] text-[#030F1C]">
            <div className="px-[20px] py-[5px]">
              <p className="mb-4">
                <strong>Yes</strong> — but only after clearing FMGE/NExT. This
                exam has a low pass rate. Miles students don’t need FMGE at all
                and can register directly in India with their U.S. license.
              </p>

              <p className="font-semibold">
                Considering Dhaka National, Jalalabad, Mainamoti, or TMSS?
              </p>
              <p className="mb-4">
                These colleges provide solid MBBS education in Bangladesh but
                lack the U.S.-level exposure, Step prep, and residency pathway
                that truly global careers demand.
              </p>

              <p className="font-semibold mt-4 mb-2">
                Bangladesh MBBS vs 🇺🇸 Miles MBBS–MD U.S. Pathway
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border font-semibold">Feature</th>
                      <th className="p-3 border font-semibold">
                        Top Bangladeshi Medical Colleges (e.g., DNMC, SWMC,
                        TMSS, Enam, Ibrahim)
                      </th>
                      <th className="p-3 border font-semibold">
                        Miles MBBS–MD U.S. Pathway
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      [
                        "Language of Instruction",
                        "English + Bangla (for clinicals)",
                        "100% English",
                      ],
                      [
                        "FMGE/NExT Requirement (India)",
                        "Yes",
                        "No (U.S. license exemption)",
                      ],
                      [
                        "U.S. Clinical Rotations",
                        "Not available",
                        "Guaranteed",
                      ],
                      ["USMLE Step 1 & 2 Prep", "No", "Yes"],
                      ["Residency Support (U.S.)", "No", "Yes"],
                      [
                        "Global Licensing",
                        "Limited to Bangladesh/India",
                        "U.S., UK, UAE, Canada, India etc",
                      ],
                      ["Tuition Cost", "₹25–35L", "₹1 Cr (₹20L/year × 5)"],
                      [
                        "Post-Residency Salary",
                        "₹10–15 LPA (India)",
                        "₹2 Cr/year (U.S.)",
                      ],
                    ].map(([feature, bd, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{feature}</td>
                        <td className="p-3 border">{bd}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="font-semibold mt-4 mb-2">Return on Investment</p>
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-200 text-left text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 border font-semibold">Metric</th>
                      <th className="p-3 border font-semibold">
                        Bangladesh MBBS
                      </th>
                      <th className="p-3 border font-semibold">
                        Miles MBBS–MD U.S. Pathway
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Total Cost", "₹25–35L", "₹1 Cr"],
                      ["FMGE/NExT", "Yes", "No (U.S. license exemption)"],
                      ["Earnings During Residency", "₹0", "₹4–6L/month"],
                      ["Post-Residency Salary", "₹10–15 LPA", "₹2 Cr/year"],
                    ].map(([metric, bd, miles], idx) => (
                      <tr key={idx} className="odd:bg-white even:bg-gray-50">
                        <td className="p-3 border">{metric}</td>
                        <td className="p-3 border">{bd}</td>
                        <td className="p-3 border">{miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );

  return (
    <div className="mx-auto w-full xl:px-0 md:px-6 lg:!px-6 px-5 relative md:bg-bottom bg-center bg-repeat bg-[length:100% 100%] md:pb-14 pb-[40px]">
      <div className="lg:container mx-auto 2xl:px-0 xl:px-10 lg:p-0 text-[#030F1C] z-20 relative">
        <h2 className="font-semibold md:text-[58px] text-[24px] text-center md:mb-[40px] mb-[30px]">
          <span className="md:pb-2 inline-block visit_txt mt-12 pr-[7px]">
            FAQs
          </span>
        </h2>

        <div className="flex flex-wrap justify-center mb-6 gap-2">
          {countries.map((item) => {
            const isActive = item === activeCountry;
            return (
              <button
                key={item}
                onClick={() => setActiveCountry(item)}
                className={`text-[16px] font-semibold px-4 py-2 rounded-full transition-all duration-300 border ${
                  isActive
                    ? "border-[#000E61] bg-gradient-to-r from-[#000E61] via-[#0060EB] to-[#3399FF] text-white shadow-lg"
                    : "border-[#0A1D8D] bg-white text-[#0A1D8D] hover:bg-[#253B80] hover:text-white hover:border-[#253B80]"
                }`}
              >
                {item}
              </button>
            );
          })}
        </div>

        {activeCountry === "GENERIC"
          ? renderGeneric()
          : renderCountry(activeCountry as CountryKey)}
      </div>
    </div>
  );
};

export default DoctorFAQs;
